import { useState, useMemo } from 'react'
import { CheckCircleIcon, XCircleIcon, ClockIcon, QuestionMarkCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid'
import { ChevronDownIcon, ChevronRightIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'
import type { ComplianceViolation, PolicyCriticality } from '@/types'
import { refMatchesFramework } from '@/lib/frameworkUtils'

interface FrameworkControlsListProps {
  policies: ComplianceViolation[]
  frameworkCode?: string
  /** Control-level counts from the framework score (e.g., 1/18) — authoritative */
  passedControls?: number
  totalControls?: number
  /** Optional partial-control count from the framework score (backend-authoritative) */
  partialControls?: number
  onAddWaiver?: (violation: ComplianceViolation) => void
  onEditWaiver?: (violation: ComplianceViolation) => void
  onDeleteWaiver?: (violation: ComplianceViolation) => void
}

type ControlStatusFilter = 'all' | 'passing' | 'failing' | 'unavailable' | 'partial'
type ControlStatus = 'passed' | 'waived' | 'failed' | 'data_unavailable' | 'partial'

/** A framework control with its constituent policy checks */
interface FrameworkControl {
  /** The full framework reference string used as the control key (matches backend exactly) */
  controlRef: string
  /** All policy checks mapped to this control */
  checks: ComplianceViolation[]
  /**
   * - "passed" = all checks Pass
   * - "waived" = no failures, at least one waiver, no data-unavailable
   * - "partial" = no failures, at least one data-unavailable AND at least one pass/waiver
   * - "data_unavailable" = only data-unavailable checks (no pass, no waiver, no failure)
   * - "failed" = any real (non-waived, non-DU) failure
   */
  status: ControlStatus
  passCount: number
  failCount: number
  waivedCount: number
  dataUnavailableCount: number
  highestCriticality: PolicyCriticality
}


// Editorial criticality pills — green/sand/red severity tints.
function getCriticalityStyles(criticality: PolicyCriticality) {
  switch (criticality) {
    case 'SHALL':
      return 'bg-[#FBE9E9] text-[#A03434] border-[#E9C9C9]'
    case 'SHOULD':
      return 'bg-[#FDF3DF] text-[#8A6B3A] border-[#E8D9B5]'
    case 'MAY':
    default:
      return 'bg-[#F5F2EA] text-[color:var(--color-mute,#6B7280)] border-[color:var(--color-rule,#E6E1D5)]'
  }
}

function StatusIcon({ status }: { status: string }) {
  const size = { width: '1.125rem', height: '1.125rem' }
  switch (status) {
    case 'Pass':
      return <CheckCircleIcon className="text-[#4A9B6E] flex-shrink-0" style={size} />
    case 'Waived':
      return <ClockIcon className="text-[#C9AD80] flex-shrink-0" style={size} />
    default:
      return <XCircleIcon className="text-[#C84B4B] flex-shrink-0" style={size} />
  }
}

function ControlStatusIcon({ status }: { status: ControlStatus }) {
  switch (status) {
    case 'passed':
      return <CheckCircleIcon className="h-5 w-5 text-[#4A9B6E] flex-shrink-0" />
    case 'waived':
      return <ClockIcon className="h-5 w-5 text-[#C9AD80] flex-shrink-0" />
    case 'data_unavailable':
      return <QuestionMarkCircleIcon className="h-5 w-5 text-[#C9AD80] flex-shrink-0" />
    case 'partial':
      // Pass+DU mix: show a pass icon with a small overlaid warning to signal
      // that the control passes only on the checks we could evaluate.
      return (
        <span className="relative inline-flex h-5 w-5 flex-shrink-0">
          <CheckCircleIcon className="h-5 w-5 text-[#4A9B6E]" />
          <ExclamationTriangleIcon className="absolute -bottom-0.5 -right-0.5 h-3 w-3 text-[#C9AD80]" />
        </span>
      )
    default:
      return <XCircleIcon className="h-5 w-5 text-[#C84B4B] flex-shrink-0" />
  }
}

const CRITICALITY_ORDER: Record<string, number> = { 'SHALL': 0, 'SHOULD': 1, 'MAY': 2 }

// Editorial chip / pill / button class fragments. Hoisted so we don't ship the
// same long Tailwind string 4-6 times.
const PILL_BASE =
  'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.16em]'
const SAND_PILL = `${PILL_BASE} bg-[#FDF3DF] text-[#8A6B3A] border border-[#E8D9B5]`
const ACTION_BTN =
  'rounded-full border border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-paper,#fff)] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-[color:var(--color-navy,#212938)] hover:bg-[color:var(--color-cream,#FBF8F2)] transition-colors'

const ROW_TONE: Record<ControlStatus, string> = {
  passed:           'border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)]',
  partial:          'border-[#E8D9B5] bg-[#FDF8EC]',
  data_unavailable: 'border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-cream,#FBF8F2)]',
  waived:           'border-[#E8D9B5] bg-[color:var(--color-paper,#fff)]',
  failed:           'border-[#E9C9C9] bg-[#FBF1F1]',
}

/**
 * Full control inventory view for a framework.
 *
 * Groups policy checks by their FULL framework reference string (matching the backend's
 * controlStatuses dictionary key). A control is "passed" only if ALL its checks have
 * status "Pass" (waived-only controls are shown as "waived", not "passed").
 */
export default function FrameworkControlsList({
  policies,
  frameworkCode,
  passedControls,
  totalControls,
  partialControls,
  onAddWaiver,
  onEditWaiver,
  onDeleteWaiver,
}: FrameworkControlsListProps) {
  const [controlFilter, setControlFilter] = useState<ControlStatusFilter>('all')
  const [expandedControlRef, setExpandedControlRef] = useState<string | null>(null)

  // Build framework controls by grouping checks under their full framework reference string.
  // This mirrors the backend's controlStatuses dictionary which uses the verbatim ref string
  // (e.g., "MCSB IM-1", "NIST SP 800-53 IA-2") as the key.
  const controls = useMemo<FrameworkControl[]>(() => {
    if (!frameworkCode) return []

    // Map: full framework reference string → list of policy checks
    const controlMap = new Map<string, ComplianceViolation[]>()

    for (const policy of policies) {
      const refs = policy.frameworkReferences ?? []
      // A single policy may map to MULTIPLE controls within the same framework
      // (e.g., a policy references both "MCSB IM-1" and "MCSB IM-6").
      // The backend counts each reference as a separate control entry.
      let matched = false

      for (const ref of refs) {
        if (refMatchesFramework(ref, frameworkCode)) {
          if (!controlMap.has(ref)) controlMap.set(ref, [])
          controlMap.get(ref)!.push(policy)
          matched = true
        }
      }

      // Skip policies with no matching framework reference — don't create ghost controls
      if (!matched) continue
    }

    return Array.from(controlMap.entries())
      .map(([controlRef, checks]): FrameworkControl => {
        let passCount = 0, failCount = 0, waivedCount = 0, dataUnavailableCount = 0
        let highestCriticality: PolicyCriticality = 'MAY'

        for (const c of checks) {
          if (c.status === 'Pass') passCount++
          else if (c.hasWaiver || c.status === 'Waived') waivedCount++
          else if (c.status === 'DataUnavailable') dataUnavailableCount++
          else {
            failCount++
            if ((CRITICALITY_ORDER[c.criticality] ?? 2) < (CRITICALITY_ORDER[highestCriticality] ?? 2)) {
              highestCriticality = c.criticality
            }
          }
        }

        // Match backend logic (five states):
        // - "failed" = any real (non-waived, non-data-unavailable) failures
        // - "partial" = no failures AND at least one data-unavailable check AND at least one pass/waiver
        // - "data_unavailable" = only data-unavailable checks
        // - "waived" = no failures, no data-unavailable, at least one waiver
        // - "passed" = all checks pass
        let status: ControlStatus
        if (failCount > 0) status = 'failed'
        else if (dataUnavailableCount > 0 && (passCount > 0 || waivedCount > 0)) status = 'partial'
        else if (dataUnavailableCount > 0) status = 'data_unavailable'
        else if (waivedCount > 0) status = 'waived'
        else status = 'passed'

        return {
          controlRef,
          checks: checks.sort((a, b) => a.policyCode.localeCompare(b.policyCode)),
          status,
          passCount,
          failCount,
          waivedCount,
          dataUnavailableCount,
          highestCriticality,
        }
      })
      .sort((a, b) => a.controlRef.localeCompare(b.controlRef))
  }, [policies, frameworkCode])

  // Use backend-authoritative counts when available, falling back to client aggregation.
  const displayPassed = passedControls ?? controls.filter(c => c.status === 'passed').length
  const displayTotal = totalControls ?? controls.length
  const displayUnavailable = controls.filter(c => c.status === 'data_unavailable').length
  const displayPartial = partialControls ?? controls.filter(c => c.status === 'partial').length
  const displayEvaluatable = displayTotal - displayUnavailable
  // Failing = evaluatable minus the buckets that count as passing (pure pass + partial).
  const displayFailing = Math.max(0, displayEvaluatable - displayPassed - displayPartial)

  // Filter controls for list display
  const filteredControls = useMemo(() => {
    if (controlFilter === 'all') return controls
    if (controlFilter === 'passing') return controls.filter(c => c.status === 'passed')
    if (controlFilter === 'partial') return controls.filter(c => c.status === 'partial')
    if (controlFilter === 'unavailable') return controls.filter(c => c.status === 'data_unavailable')
    // "failing" includes failed and waived-only (anything not passed/partial/data_unavailable)
    return controls.filter(c => c.status === 'failed' || c.status === 'waived')
  }, [controls, controlFilter])

  const filterPills: { key: ControlStatusFilter; label: string; count: number }[] = [
    { key: 'all', label: 'All Controls', count: displayTotal },
    { key: 'passing', label: 'Passing', count: displayPassed },
    { key: 'failing', label: 'Failing', count: displayFailing },
    ...(displayPartial > 0 ? [{ key: 'partial' as const, label: 'Partial', count: displayPartial }] : []),
    ...(displayUnavailable > 0 ? [{ key: 'unavailable' as const, label: 'Unavailable', count: displayUnavailable }] : []),
  ]

  // Coverage bar (four segments: pass / partial / fail / unavailable)
  const passPct = displayTotal > 0 ? (displayPassed / displayTotal) * 100 : 0
  const partialPct = displayTotal > 0 ? (displayPartial / displayTotal) * 100 : 0
  const failPct = displayTotal > 0 ? (displayFailing / displayTotal) * 100 : 0
  const unavailPct = displayTotal > 0 ? (displayUnavailable / displayTotal) * 100 : 0

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-[color:var(--color-navy,#212938)]">
            {displayPassed + displayPartial}/{displayEvaluatable} controls passing
            {displayPartial > 0 && (
              <span className="ml-1 text-xs italic text-[#8A6B3A]">
                (incl. {displayPartial} partial)
              </span>
            )}
          </span>
          <div className="flex items-center gap-4 text-[10px] font-bold uppercase tracking-[0.16em] text-[color:var(--color-faint,#9CA3AF)]">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-[#4A9B6E]" />
              Passing ({displayPassed})
            </span>
            {displayPartial > 0 && (
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-[#4A9B6E]/60 ring-1 ring-[#C9AD80]" />
                Partial ({displayPartial})
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-[#C84B4B]" />
              Failing ({displayFailing})
            </span>
            {displayUnavailable > 0 && (
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-[#C9AD80]/70" />
                Unavailable ({displayUnavailable})
              </span>
            )}
          </div>
        </div>
        <div className="h-2 bg-[color:var(--color-rule,#E6E1D5)] rounded-full overflow-hidden flex">
          {passPct > 0 && (
            <div
              className="h-full bg-[#4A9B6E] transition-all duration-500"
              style={{ width: `${passPct}%` }}
            />
          )}
          {partialPct > 0 && (
            <div
              className="h-full transition-all duration-500"
              style={{
                width: `${partialPct}%`,
                backgroundColor: 'rgba(74, 155, 110, 0.6)',
                backgroundImage: 'repeating-linear-gradient(45deg, transparent 0 4px, rgba(201, 173, 128, 0.55) 4px 8px)',
              }}
            />
          )}
          {failPct > 0 && (
            <div
              className="h-full bg-[#C84B4B] transition-all duration-500"
              style={{ width: `${failPct}%` }}
            />
          )}
          {unavailPct > 0 && (
            <div
              className="h-full transition-all duration-500"
              style={{ width: `${unavailPct}%`, backgroundColor: 'rgba(201, 173, 128, 0.55)' }}
            />
          )}
        </div>
        <p className="text-[12px] italic text-[color:var(--color-mute,#6B7280)]">
          Each control may have multiple policy checks. Partial controls pass on the checks we
          could evaluate but have additional checks whose data wasn't available (e.g., license-gated).
        </p>
      </div>

      {/* Active = solid navy chip; inactive = white + rule border. Mirrors the
          filter chips in BestPracticeAnalyzerTab.tsx Section 01. */}
      <div className="flex flex-wrap gap-2 text-[10px] font-bold uppercase tracking-[0.16em]">
        {filterPills.map(pill => {
          const isActive = controlFilter === pill.key
          return (
            <button
              key={pill.key}
              type="button"
              onClick={() => setControlFilter(pill.key)}
              className={[
                'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 transition-colors',
                isActive
                  ? 'border-[color:var(--color-navy,#212938)] bg-[color:var(--color-navy,#212938)] text-[color:var(--color-paper,#fff)]'
                  : 'border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-paper,#fff)] text-[color:var(--color-navy,#212938)] hover:bg-[color:var(--color-cream,#FBF8F2)]',
              ].join(' ')}
            >
              {pill.label}
              <span
                className={[
                  'rounded-full px-1.5 py-0.5 text-[10px] tabular-nums',
                  isActive ? 'bg-[color:var(--color-paper,#fff)]/20 text-[color:var(--color-paper,#fff)]' : 'bg-[color:var(--color-cream,#FBF8F2)] text-[color:var(--color-mute,#6B7280)]',
                ].join(' ')}
              >
                {pill.count}
              </span>
            </button>
          )
        })}
      </div>

      {filteredControls.length === 0 ? (
        <div className="rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] py-8 text-center text-[13px] italic text-[color:var(--color-mute,#6B7280)]">
          No controls match the selected filter.
        </div>
      ) : (
        <div className="space-y-1.5">
          {filteredControls.map(control => {
            const isExpanded = expandedControlRef === control.controlRef
            const totalChecks = control.checks.length

            return (
              <div
                key={control.controlRef}
                className={`rounded-[4px] border overflow-hidden transition-colors ${ROW_TONE[control.status]}`}
              >
                <button
                  onClick={() => setExpandedControlRef(isExpanded ? null : control.controlRef)}
                  className={`w-full text-left flex items-center gap-3 px-4 py-3 transition-colors group ${
                    isExpanded ? 'bg-[color:var(--color-cream,#FBF8F2)]' : 'hover:bg-[color:var(--color-cream,#FBF8F2)]'
                  }`}
                >
                  <ControlStatusIcon status={control.status} />

                  <span className="text-sm font-mono text-[#4F86A8] flex-shrink-0 font-medium">
                    {control.controlRef}
                  </span>

                  <span className="text-[11px] tabular-nums text-[color:var(--color-mute,#6B7280)] flex-shrink-0">
                    {control.passCount}/{totalChecks} checks passing
                  </span>

                  <span className="flex-1" />

                  {control.status === 'waived' && (
                    <span className={`${SAND_PILL} flex-shrink-0`}>Waived</span>
                  )}

                  {control.status === 'partial' && (
                    <span
                      className={`${SAND_PILL} flex-shrink-0`}
                      title={`${control.dataUnavailableCount} of ${control.checks.length} checks could not be evaluated (e.g., required license or data missing)`}
                    >
                      Passed · {control.dataUnavailableCount} not evaluated
                    </span>
                  )}

                  {control.status === 'data_unavailable' && (
                    <span className={`${PILL_BASE} bg-[color:var(--color-cream,#FBF8F2)] text-[color:var(--color-mute,#6B7280)] border border-[color:var(--color-rule,#E6E1D5)] flex-shrink-0`}>
                      Data Unavailable
                    </span>
                  )}

                  {control.status === 'failed' && (
                    <span className={`${PILL_BASE} border flex-shrink-0 ${getCriticalityStyles(control.highestCriticality)}`}>
                      {control.highestCriticality}
                    </span>
                  )}

                  {isExpanded ? (
                    <ChevronDownIcon className="h-4 w-4 text-[color:var(--color-faint,#9CA3AF)] flex-shrink-0" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4 text-[color:var(--color-faint,#9CA3AF)] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  )}
                </button>

                {isExpanded && (
                  <div className="px-4 pb-3 space-y-1 border-t border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)]">
                    <div className="pt-3 pb-1">
                      <span className="text-[10px] font-bold text-[color:var(--color-faint,#9CA3AF)] uppercase tracking-[0.18em]">
                        Policy checks ({totalChecks})
                      </span>
                    </div>
                    {control.checks.map(check => (
                      <div
                        key={check.id}
                        className="flex items-start gap-2.5 px-3 py-2 rounded-[4px] bg-[color:var(--color-cream,#FBF8F2)] border border-[color:var(--color-rule,#E6E1D5)]"
                      >
                        <div className="mt-0.5">
                          <StatusIcon status={check.status} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-mono text-[#4F86A8]">
                              {check.policyCode}
                            </span>
                            <span className={`${PILL_BASE} border ${getCriticalityStyles(check.criticality)}`}>
                              {check.criticality}
                            </span>
                            {check.hasWaiver && (
                              <span className={SAND_PILL}>Waived</span>
                            )}
                          </div>
                          <p className="text-sm font-medium text-[color:var(--color-navy,#212938)] mt-0.5">{check.policyTitle}</p>
                          <p className="text-xs text-[color:var(--color-mute,#6B7280)] mt-0.5 line-clamp-2">{check.message}</p>

                          {check.frameworkReferences?.length > 0 && (
                            <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                              <ShieldCheckIcon className="h-3 w-3 text-[#4F86A8] flex-shrink-0" />
                              {check.frameworkReferences.map((ref, i) => (
                                <span key={i} className="text-[10px] font-mono bg-[color:var(--color-paper,#fff)] text-[color:var(--color-mute,#6B7280)] border border-[color:var(--color-rule,#E6E1D5)] px-1.5 py-0.5 rounded">
                                  {ref}
                                </span>
                              ))}
                            </div>
                          )}

                          {check.status !== 'Pass' && check.remediationSteps && (
                            <div className="mt-2">
                              <span className="text-[10px] font-bold text-[color:var(--color-faint,#9CA3AF)] uppercase tracking-[0.18em]">Remediation</span>
                              <p className="text-xs text-[color:var(--color-navy,#212938)] mt-0.5">{check.remediationSteps}</p>
                            </div>
                          )}

                          {(check.remediationUrl || (!check.hasWaiver && check.status === 'Open') || check.hasWaiver) && (
                            <div className="flex items-center gap-2 mt-2.5">
                              {check.remediationUrl && check.status !== 'Pass' && (
                                <a
                                  href={check.remediationUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`inline-flex items-center gap-1 ${ACTION_BTN}`}
                                >
                                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                  </svg>
                                  Microsoft Learn
                                </a>
                              )}
                              {check.status === 'Open' && !check.hasWaiver && onAddWaiver && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    onAddWaiver(check)
                                  }}
                                  className={ACTION_BTN}
                                >
                                  Add waiver
                                </button>
                              )}
                              {check.hasWaiver && onEditWaiver && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    onEditWaiver(check)
                                  }}
                                  className={ACTION_BTN}
                                >
                                  Edit waiver
                                </button>
                              )}
                              {check.hasWaiver && onDeleteWaiver && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    onDeleteWaiver(check)
                                  }}
                                  className="rounded-full border border-[#E9C9C9] bg-[color:var(--color-paper,#fff)] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-[#A03434] hover:bg-[#FBF1F1] transition-colors"
                                >
                                  Remove waiver
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
